import React from 'react';

import styled from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import ElapsedTime from 'app/components/general/ElapsedTime';
import StateButton from 'app/general/widgets/StateButton';
import {fetchKBArticle, updateArticleFeedback} from 'app/kbs/actions';
import {showToastNotification} from 'app/toasts/slice';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import type {
  BasicUserSerializedType,
  KBArticleFeedbackSerializedType,
} from 'sweet-types';

interface Props {
  feedback: KBArticleFeedbackSerializedType;
}

export function Feedback({feedback}: Props): JSX.Element {
  const [message, setMessage] = React.useState('');
  const [reply, setReply] = React.useState(false);
  const [isResolving, setIsResolving] = React.useState(false);
  const [status, setStatus] = React.useState<'done' | 'ignored'>('done');

  const actions = useAsyncActions({
    updateArticleFeedback,
    fetchKBArticle,
    showToastNotification,
  });

  function toggleResolve() {
    setIsResolving((r) => !r);
    setMessage('');
    setReply(false);
    setStatus('done');
  }

  return (
    <div className="border-bottom">
      <div className="d-flex py-2 align-items-center">
        <div className="align-self-start mr-3">
          {feedback.user == null ? (
            <OpaqueDiv>
              <UserAvatar user={ANONYMOUS_USER} />
            </OpaqueDiv>
          ) : (
            <UserAvatar showCard user={feedback.user} />
          )}
        </div>
        <div className="small flex-grow-1">
          <div>
            {feedback.rating === 5 ? (
              <div className="text-success">
                <i className="mr-2 fas fa-thumbs-up" />
                {gt('Answered my question')}
              </div>
            ) : (
              <div className="text-danger">
                <i className="mr-2 fas fa-thumbs-down" />
                {gt('Did not answer my question')}
              </div>
            )}
          </div>
          {feedback.comment ? (
            <div className="mt-2 mb-1">{feedback.comment}</div>
          ) : null}
          <div>
            <ElapsedTime
              timestamp={feedback.created_at}
              tooltipPlacement="right"
              ContainerTag="span"
              small={true}
              className="text-secondary font-italic mt-1"
            />
          </div>
        </div>
        {feedback.admin_comment == null && !isResolving ? (
          <div className="align-self-end my-1 ml-2">
            <button
              className="btn btn-sm btn-light"
              onClick={() => toggleResolve()}
            >
              {gt('Resolve')}
            </button>
          </div>
        ) : null}
      </div>
      {feedback.admin_comment != null && !isResolving ? (
        <ManagerResolveDiv>
          <div className="d-flex align-items-center">
            <div className="ml-2 mr-3">
              <i className="far fa-comment" />
            </div>
            <div className="flex-grow-1 small">
              {feedback.admin_comment !== '' ? (
                feedback.admin_comment
              ) : (
                <span className="font-italic text-black-50">
                  {gt('No reply was provided')}
                </span>
              )}
            </div>
            <div className="ml-2">
              <UserAvatar
                showCard
                tooltipTitle={gt('Resolved by')}
                user={feedback.admin_comment_user ?? ANONYMOUS_USER}
              />
            </div>
          </div>
        </ManagerResolveDiv>
      ) : null}

      {isResolving ? (
        <ManagerResolveDiv>
          {feedback.can_inform ? (
            <div>
              <label className="d-flex align-items-center mt-2 mb-3">
                <input
                  type="checkbox"
                  checked={reply}
                  onChange={() => {
                    setReply((reply) => !reply);
                    setMessage('');
                  }}
                />
                <span className="small ml-1">
                  {gt('Reply to the feedback author.')}
                </span>
              </label>

              {reply ? (
                <div className="small mb-2">
                  {gt('What would you like to say?')}
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    onChange={(e) => setMessage(e.target.value)}
                    value={message}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div className="small mt-2 mb-3">
              {gt("Feedback author didn't leave their email.")}
            </div>
          )}

          <div>
            <div className="form-text text-muted small font-weight-bold">
              {gt(
                'Resolved feedback will no longer be counted towards article score'
              )}
            </div>
            <div className="d-flex justify-content-end border-top pt-2 mt-2">
              <button className="btn btn-link btn-sm" onClick={toggleResolve}>
                {gt('Cancel')}
              </button>
              <StateButton
                className="btn btn-primary btn-sm"
                onClick={() => {
                  return actions
                    .updateArticleFeedback(feedback, {
                      admin_comment: message,
                      status: 'done',
                      inform_user: reply,
                    })
                    .then(
                      () => {
                        toggleResolve();
                        actions.fetchKBArticle(feedback.article);
                      },
                      () =>
                        actions.showToastNotification(
                          gt(
                            "Couldn't mark the feedback as resolved at the moment."
                          ),
                          {level: 'error'}
                        )
                    );
                }}
                busyText={
                  reply
                    ? gt('Resolve & Send')
                    : status == 'ignored'
                    ? gt('Ignore')
                    : gt('Mark as Resolved')
                }
              >
                {reply
                  ? gt('Resolve & Send')
                  : status == 'ignored'
                  ? gt('Ignore')
                  : gt('Mark as Resolved')}
              </StateButton>
            </div>
          </div>
        </ManagerResolveDiv>
      ) : null}
    </div>
  );
}

const OpaqueDiv = styled.div`
  opacity: 0.4;
`;

export const ManagerResolveDiv = styled.div.attrs({
  className: 'mb-2 px-2 py-2 border rounded-sm',
})`
  background-color: #f9f9f9;
`;

const ANONYMOUS_USER: BasicUserSerializedType = {
  id: 0,
  url: '',
  name: 'Anonymous',
  email: '',
  content_type: 'user',
  avatar_image: null,
  is_deleted: false,
};
